import { camelCase } from "./strings"

/**
 * validate custom size prop
 *
 * @param {Array} sizeArr array of sizes which the component accepts
 * @param {String} size size of component
 */
export const validateSize = (sizeArr, size) => {
  return sizeArr.includes(size)
}

/**
 * validates that specific props occur only once
 *
 * takes an Enum of props to be validated and returns a function
 * the returned function then accepts props and checks that the
 * props which are in the Enum occur only once.
 *
 * @param {Enumerator} propsEnum Enum of props to be validated
 *
 * @returns {Function}
 */
export const createPropOccurValidator = (propsEnum) => (props) => {
  const incomingProps = Object.keys(props).filter((key) => Boolean(props[key]))
  return propsEnum.filter((prop) => incomingProps.includes(prop)).length <= 1
}

/**
 * Checks and resolves colliding props if present.
 * Returns defaultProp in case of collision otherwise return the prop.
 *
 * @param {Object} propsObj values of the props
 * @param {String} defaultProp fallback prop
 * @returns {{collision: boolean, value: string}} value with singleOccur information
 */
export const resolvePropCollision = (propsObj, defaultProp) => {
  const validateProp = createPropOccurValidator(Object.keys(propsObj))

  const singleOccur = validateProp({ ...propsObj })

  if (!singleOccur) {
    return { collision: true, value: defaultProp }
  }

  const truthyProp = Object.keys(propsObj).find((prop) => propsObj[prop])

  return { collision: false, value: truthyProp ? truthyProp : defaultProp }
}

/**
 * returns function to determine support type
 *
 * @param {String} defaultType default support type
 *
 * @returns {Function}
 */
export const getSupportType = (defaultType) => (typeProps) => {
  /**
   * determines support type for component
   *
   * example:
   * let's say `danger` is the default support type
   * _case 1_: pass `info` in prop, returns `info`
   * _case 2_: pass `info` and `danger`, returns `info`
   * _case 3_: pass `info`, `success` and `danger`, returns `danger`
   *
   * @param {Object} typeProps object of support types
   *
   * @return {string}
   */
  const { [defaultType]: removedProp, ...restProps } = typeProps
  const supportKeys = Object.keys(restProps)
  const doPropsConflict = !createPropOccurValidator(supportKeys)(restProps)
  if (doPropsConflict) {
    return defaultType
  }
  return supportKeys.find((key) => Boolean(restProps[key])) || defaultType
}

/**
 * Returns a function to merge refs
 *
 * @param  {...any} refs React Ref to merge
 * @returns {Function}
 */
export const mergeRefs =
  (...refs) =>
  (node) => {
    /**
     * Points all the refs to one element
     *
     * @param {Object} node element to which all refs will point
     */
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node)
      } else if (ref !== null) {
        ref.current = node
      }
    })
  }

/**
 * Returns properties with desired prefix from an object
 * @private
 *
 * @param {object} obj Object to filter from
 * @param {string} prefix prefix of properties to return
 *
 * @return object
 */
const yieldPropsWithPrefix = (obj, prefix) =>
  Object.keys(obj).reduce(
    (prefixedProps, key) => ({
      ...prefixedProps,
      ...(key.startsWith(prefix) && { [key]: obj[key] }),
    }),
    {}
  )

/**
 * Filters out properties with undesired prefix from an object
 * @private
 *
 * @param {object} obj Object to filter from
 * @param {string} prefix prefix of properties to filter
 *
 * @return object
 */
const filterPropsWithPrefix = (obj, prefix) =>
  Object.keys(obj).reduce(
    (prefixedProps, key) => ({
      ...prefixedProps,
      ...(!key.startsWith(prefix) && { [key]: obj[key] }),
    }),
    {}
  )

/**
 * Returns properties with `data-` prefix from an object
 *
 * @param {object} obj Object to filter from
 * @returns {object}
 */
export const yieldDataAttrs = (obj) => yieldPropsWithPrefix(obj, "data-")

/**
 * Excludes properties with `data-` prefix from on object
 *
 * @param {object} obj Object to filter from
 * @returns {object}
 */
export const excludeDataAttrs = (obj) => filterPropsWithPrefix(obj, "data-")

/**
 * Returns properties with `aria-` prefix from an object
 *
 * @param {object} obj Object to filter from
 * @returns {object}
 */
export const yieldAriaAttrs = (obj) => yieldPropsWithPrefix(obj, "aria-")

/**
 * Excludes properties with `aria-` prefix from on object
 *
 * @param {object} obj Object to filter from
 * @returns {object}
 */
export const excludeAriaAttrs = (obj) => filterPropsWithPrefix(obj, "aria-")

export const concatClasses = (...words) => camelCase(...words)
